import React from 'react';
import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { isEmpty, isNil } from 'lodash';

import { useActionDocumentTemplate } from '~/components/AiChat/hooks/useActionDocumentTemplate';
import type { AiAction } from '~/components/AiChat/types';
import { isDynamicClaimActionTaskData } from '~/components/AiChat/types';
import { useClaim } from '~/components/ClaimContainer';
import DocumentPdfPreview from '~/components/core/DocumentPdfPreview';
import useDataFetcher from '~/components/useDataFetcher';
import cn from '~/Utils/cn';

import type { ActionPreviewState } from './types';

// Gunicorn refuses a request line over 4094 bytes; the preview carries every field in its query.
const MAX_PREVIEW_REQUEST_LINE_BYTES = 4000;

const PREVIEW_UNAVAILABLE_NOTE = 'The document preview is unavailable.';
const PREVIEW_TOO_LONG_NOTE = 'Preview unavailable: the letter text is too long to preview here.';
const NO_TEMPLATE_NOTE = 'Preview unavailable: no template on this claim matches the one this action names.';
const AMBIGUOUS_TEMPLATE_NOTE =
  'Preview unavailable: more than one template on this claim matches the one this action names.';

interface DocumentTemplatePreview {
  document_base64: string;
  is_missing: boolean;
}

interface TemplateFieldValue {
  field_id: string;
  value: string;
}

const FETCH_OPTIONS = { shouldOpenErrorDialog: false };

const asFieldValues = (fields: unknown): TemplateFieldValue[] => (Array.isArray(fields) ? fields : []);

// The preview is a GET whose query carries every field, so a long letter can outgrow the line the server accepts.
const exceedsRequestLineLimit = (url: string, config: AxiosRequestConfig) =>
  `GET ${axios.getUri({ url, ...config })} HTTP/1.1`.length > MAX_PREVIEW_REQUEST_LINE_BYTES;

/**
 * The document a pending create-document-from-template card will file, rendered through the
 * template picker's own preview of the template `useActionDocumentTemplate` resolves the card to.
 */
export const useDocumentTemplatePreview = (action: AiAction, className?: string): ActionPreviewState => {
  const { claim } = useClaim({ claimRequired: true });
  const payload = isDynamicClaimActionTaskData(action.task_data) ? action.task_data.envelope?.payload : undefined;

  const { exposureId, template, matches, isLoading: areTemplatesLoading, isListed } = useActionDocumentTemplate(action);

  const previewUrl = template
    ? `/api/v1/claims/${claim.id}/documents/document_templates/${template.doc_id}/preview`
    : null;
  const previewConfig = React.useMemo(
    () => ({
      params: {
        exposure_id: exposureId,
        ...Object.fromEntries(asFieldValues(payload?.fields).map((field) => [field.field_id, field.value])),
      },
    }),
    [exposureId, payload]
  );
  const isTooLong = !isNil(previewUrl) && exceedsRequestLineLimit(previewUrl, previewConfig);
  const shouldFetchPreview = !isNil(previewUrl) && !isTooLong;

  const preview = useDataFetcher<DocumentTemplatePreview>(shouldFetchPreview ? previewUrl : null, previewConfig, {
    ...FETCH_OPTIONS,
    shouldFetch: shouldFetchPreview,
  });

  // The preview's fetch starts a render after the template resolves, so it is pending until it answers.
  const isPreviewPending = shouldFetchPreview && !preview.data && !preview.isError;
  if (areTemplatesLoading || preview.isLoading || isPreviewPending) return { isLoading: true };
  if (isListed && !template) {
    // The action refuses both cases too, each with its own error.
    return { isLoading: false, unavailableNote: isEmpty(matches) ? NO_TEMPLATE_NOTE : AMBIGUOUS_TEMPLATE_NOTE };
  }
  if (isTooLong) return { isLoading: false, unavailableNote: PREVIEW_TOO_LONG_NOTE };
  if (!preview.data || preview.isError || preview.data.is_missing) {
    return { isLoading: false, unavailableNote: PREVIEW_UNAVAILABLE_NOTE };
  }
  return {
    isLoading: false,
    content: (
      <DocumentPdfPreview
        className={cn('h-[480px] w-full', className)}
        label="Document preview"
        base64={preview.data.document_base64}
      />
    ),
  };
};
